import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, withModifiers as _withModifiers, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mutate-entity-modal-body" }
const _hoisted_2 = {
  key: 0,
  class: "readonly-field"
}
const _hoisted_3 = { class: "readonly-field-value" }
const _hoisted_4 = {
  key: 3,
  class: "form-error"
}
const _hoisted_5 = {
  key: 0,
  class: "loading apollo"
}

import { computed, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import Modal from "@/components/Modal.vue";
import FormItem from "@/components/form/FormItem.vue";
import BasicButton from "@/components/BasicButton.vue";

import {
  GET_EMPLOYEE_PROJECTS,
  GET_PROJECT_THEMES,
} from "@/graphql/project/queries";
import { GET_TIMESHEET_ROLES } from "@/graphql/timesheets/queries";
import {
  CREATE_TIMESHEET,
  DELETE_TIMESHEET,
  UPDATE_TIMESHEET,
  UPDATE_TIMESHEET_THEME,
} from "@/graphql/timesheets/mutations";

import enUK from "@/locales/home/en-UK.json";
import type { TimesheetFormValue } from "@/helpers/client/typesDefinition";
import type {
  Project,
  ProjectRole,
  Theme,
} from "@/helpers/project/typesDefinition";
import { formatMinutesAsTime, parseTimeToMinutes } from "@/helpers/utils";

interface SelectOption {
  id: string;
  name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditTimesheet',
  props: {
  timesheet: {
    type: Object as PropType<TimesheetFormValue>,
    required: true,
  },
  themeOnly: {
    type: Boolean,
    default: false,
  },
},
  emits: ["close", "update:timesheet", "delete"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

const selectedProjectId = ref("");
const selectedThemeId = ref("");
const selectedRoleId = ref("");
const duration = ref<string>("00:00");
const notes = ref("");
const activityId = ref("");

function applyTimesheetToForm(timesheet: TimesheetFormValue): void {
  selectedProjectId.value = timesheet.projectId ?? "";
  selectedThemeId.value = timesheet.themeId ?? "";
  selectedRoleId.value = timesheet.roleId ?? "";
  duration.value = formatMinutesAsTime(timesheet.duration);
  notes.value = timesheet.notes ?? "";
  activityId.value = timesheet.activityId ?? "";
}

applyTimesheetToForm(props.timesheet);

const readonlyUserName = computed(() => {
  return props.timesheet.userName || "Current user";
});

// In themeOnly mode, use projectId directly from timesheet and skip employeeProjects query
const projectQueryVariables = computed(() => ({
  offset: 0,
  limit: 100,
  userId: props.timesheet.userId || undefined,
}));

const {
  result: projectsResult,
  loading: projectsLoading,
  onError: onProjectsError,
  refetch: refetchProjects,
} = useQuery(GET_EMPLOYEE_PROJECTS, projectQueryVariables, {
  fetchPolicy: "network-only",
  enabled: !props.themeOnly,
});

onProjectsError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getProjects", { message: error }));
});

const projects = computed<Project[]>(() => {
  return projectsResult.value?.employeeProjects?.items ?? [];
});

const projectOptions = computed<SelectOption[]>(() => {
  // In themeOnly mode, return empty array since we don't need project selection
  if (props.themeOnly) return [];
  return projects.value.map((project: Project) => ({
    id: project.id,
    name: project.name,
  }));
});

// In themeOnly mode, pre-populate selectedProjectId from timesheet
if (props.themeOnly && props.timesheet.projectId) {
  selectedProjectId.value = props.timesheet.projectId;
}

const projectThemesQueryVariables = computed(() => ({
  id: selectedProjectId.value || undefined,
}));

const {
  result: projectThemesResult,
  loading: themesLoading,
  onError: onThemesError,
  refetch: refetchThemes,
} = useQuery(GET_PROJECT_THEMES, projectThemesQueryVariables, () => ({
  fetchPolicy: "network-only",
  enabled: Boolean(selectedProjectId.value),
}));

onThemesError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getThemes", { message: error }));
});

const projectThemes = computed<Theme[]>(() => {
  return projectThemesResult.value?.project?.themes ?? [];
});

const availableThemes = computed<Theme[]>(() => {
  return projectThemes.value;
});

const themeOptions = computed<SelectOption[]>(() => {
  return availableThemes.value.map((theme: Theme) => ({
    id: theme.id,
    name: theme.name,
  }));
});

const roleQueryVariables = computed(() => ({
  projectId: selectedProjectId.value,
  userId: props.timesheet.userId || undefined,
  date: props.timesheet.date,
}));

const {
  result: rolesResult,
  loading: rolesLoading,
  onError: onRolesError,
  refetch: refetchRoles,
} = useQuery(GET_TIMESHEET_ROLES, roleQueryVariables, {
  fetchPolicy: "network-only",
});

onRolesError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getTimersError", { message: error }));
});

const roles = ref<ProjectRole[]>([]);

const roleOptions = computed<SelectOption[]>(() => {
  return roles.value.map((role: ProjectRole) => ({
    id: role.id,
    name: role.name,
  }));
});

const selectedProjectHasNoRoles = computed(() => {
  return (
    Boolean(selectedProjectId.value) &&
    !rolesLoading.value &&
    roles.value.length === 0
  );
});

function syncSelectedRole(): void {
  if (!roles.value.length) {
    // Don't clear selectedRoleId if roles aren't loaded yet
    return;
  }

  if (!selectedRoleId.value) {
    selectedRoleId.value = roles.value[0].id;
    return;
  }

  const selectedRoleStillAvailable = roles.value.some((role) => {
    return role.id === selectedRoleId.value;
  });

  if (!selectedRoleStillAvailable) {
    selectedRoleId.value = roles.value[0].id;
  }
}

async function fetchRolesForSelectedProject(): Promise<void> {
  if (!selectedProjectId.value) {
    roles.value = [];
    return;
  }

  const result = await refetchRoles({
    projectId: selectedProjectId.value,
    userId: props.timesheet.userId || undefined,
    date: props.timesheet.date,
  });

  roles.value = [...(result?.data?.timesheetRoles ?? [])];
  syncSelectedRole();
}

async function fetchThemesForSelectedProject(): Promise<void> {
  if (!selectedProjectId.value) {
    return;
  }

  await refetchThemes({
    id: selectedProjectId.value,
  });
}

watch(
  () => rolesResult.value?.timesheetRoles,
  (newRoles) => {
    if (!selectedProjectId.value) {
      return;
    }

    roles.value = [...(newRoles ?? [])];
    syncSelectedRole();
  },
  { immediate: true },
);

watch(
  () =>
    [
      props.timesheet.id ?? "",
      props.timesheet.userId ?? "",
      props.timesheet.date,
      props.timesheet.projectId ?? "",
      props.timesheet.themeId ?? "",
      props.timesheet.roleId ?? "",
    ].join("-"),
  async () => {
    applyTimesheetToForm(props.timesheet);
    roles.value = [];

    // Skip refetchProjects in themeOnly mode - we don't need the project list
    if (!props.themeOnly) {
      await refetchProjects({
        offset: 0,
        limit: 100,
        userId: props.timesheet.userId || undefined,
      });
    }

    if (selectedProjectId.value) {
      try {
        await fetchRolesForSelectedProject();
        await fetchThemesForSelectedProject();
      } catch (error) {
        logErrorMessages(error);
        toast.error(t("errors.getTimersError", { message: error }));
      }
    }
  },
);

watch(projects, (availableProjects) => {
  if (projectsLoading.value || !projectsResult.value) {
    return;
  }

  if (!selectedProjectId.value) {
    return;
  }

  const projectStillAvailable = availableProjects.some((project) => {
    return project.id === selectedProjectId.value;
  });

  if (!projectStillAvailable) {
    selectedProjectId.value = "";
    selectedThemeId.value = "";
    selectedRoleId.value = "";
    roles.value = [];
  }
});

watch(
  selectedProjectId,
  async (newProjectId, oldProjectId) => {
    if (oldProjectId !== undefined && newProjectId !== oldProjectId) {
      selectedThemeId.value = "";
      selectedRoleId.value = "";
    }

    if (!newProjectId) {
      roles.value = [];
      return;
    }

    try {
      await fetchRolesForSelectedProject();
      await fetchThemesForSelectedProject();
    } catch (error) {
      logErrorMessages(error);
      toast.error(t("errors.getTimersError", { message: error }));
    }
  },
  { immediate: true },
);

watch(
  availableThemes,
  (themes) => {
    if (themesLoading.value) {
      return;
    }

    if (!selectedProjectId.value) {
      return;
    }

    if (!selectedThemeId.value && themes.length === 1) {
      selectedThemeId.value = themes[0].id;
      return;
    }

    if (!selectedThemeId.value) {
      return;
    }

    const selectedThemeStillAvailable = themes.some((theme) => {
      return theme.id === selectedThemeId.value;
    });

    if (!selectedThemeStillAvailable) {
      selectedThemeId.value = "";
    }
  },
  { immediate: true },
);
computed(() => {
  return !isLoading.value && Boolean(selectedThemeId.value);
});
const {
  mutate: createTimesheet,
  loading: creationLoading,
  onError: onCreateTimesheetError,
} = useMutation(CREATE_TIMESHEET);

onCreateTimesheetError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createTimesheet", { message: error }));
});

const {
  mutate: updateTimesheet,
  loading: updateLoading,
  onError: onUpdateTimesheetError,
} = useMutation(UPDATE_TIMESHEET);

onUpdateTimesheetError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateTimesheet", { message: error }));
});

const {
  mutate: updateTimesheetTheme,
  loading: updateThemeLoading,
  onError: onUpdateTimesheetThemeError,
} = useMutation(UPDATE_TIMESHEET_THEME);

onUpdateTimesheetThemeError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateTimesheet", { message: error }));
});

const {
  mutate: deleteTimesheetMutation,
  loading: deleteLoading,
  onError: onDeleteTimesheetError,
  onDone: onDeleteTimesheetDone,
} = useMutation(DELETE_TIMESHEET);

onDeleteTimesheetError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteTimesheet", { message: error }));
});

onDeleteTimesheetDone(() => {
  emit("delete");
  emit("close");
});

const isLoading = computed(() => {
  return (
    projectsLoading.value ||
    themesLoading.value ||
    rolesLoading.value ||
    creationLoading.value ||
    updateLoading.value ||
    updateThemeLoading.value ||
    deleteLoading.value
  );
});

async function submitForm(): Promise<void> {
  if (props.themeOnly) {
    // Theme-only mode for delivery managers
    if (!selectedThemeId.value) {
      toast.error(t("errors.requiredFields"));
      return;
    }

    try {
      const result = await updateTimesheetTheme({
        id: props.timesheet.id,
        themeId: selectedThemeId.value,
        projectId: props.timesheet.projectId,
      });

      const response = result?.data?.updateTimesheetTheme;

      if (!response || response.status !== "ok") {
        toast.error(response?.error ?? t("errors.updateTimesheet"));
        return;
      }

      emit("update:timesheet", response.data);
      emit("close");
      return;
    } catch (error) {
      logErrorMessages(error);
      toast.error(
        error instanceof Error ? error.message : t("errors.updateTimesheet"),
      );
      return;
    }
  }

  if (selectedProjectHasNoRoles.value) {
    toast.error("This user has no active role on the selected project.");
    return;
  }

  if (
    !selectedProjectId.value ||
    !selectedThemeId.value ||
    !selectedRoleId.value
  ) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  const parsedDuration = parseTimeToMinutes(duration.value);

  if (!parsedDuration || parsedDuration <= 0) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  const mutationVariables = {
    duration: parsedDuration,
    notes: notes.value,
    userId: props.timesheet.userId || undefined,
    projectId: selectedProjectId.value,
    themeId: selectedThemeId.value,
    roleId: selectedRoleId.value,
    activityId: activityId.value || null,
  };

  try {
    if (props.timesheet.id) {
      const result = await updateTimesheet({
        ...mutationVariables,
        id: props.timesheet.id,
      });

      const response = result?.data?.updateTimesheet;

      if (!response || response.status !== "ok") {
        toast.error(response?.error ?? t("errors.updateTimesheet"));
        return;
      }

      emit("update:timesheet", response.data);
      emit("close");
      return;
    }

    const result = await createTimesheet({
      ...mutationVariables,
      date: props.timesheet.date,
    });

    const response = result?.data?.createTimesheet;

    if (!response || response.status !== "ok") {
      toast.error(response?.error ?? t("errors.createTimesheet"));
      return;
    }

    emit("update:timesheet", response.data);
    emit("close");
  } catch (error) {
    logErrorMessages(error);
    toast.error(
      error instanceof Error ? error.message : t("errors.createTimesheet"),
    );
  }
}

async function handleDeleteTimesheet(): Promise<void> {
  if (!props.timesheet.id) {
    emit("close");
    return;
  }

  await deleteTimesheetMutation({ id: props.timesheet.id });
}

function closeModal(): void {
  emit("close");
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, { onClose: closeModal }, {
    header: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("timesheets.modal.title")), 1)
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("form", {
          onSubmit: _withModifiers(submitForm, ["prevent"]),
          id: "mutate-form"
        }, [
          (!__props.themeOnly)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "readonly-field-label" }, "User", -1)),
                _createElementVNode("div", _hoisted_3, _toDisplayString(readonlyUserName.value), 1)
              ]))
            : _createCommentVNode("", true),
          (!__props.themeOnly)
            ? (_openBlock(), _createBlock(FormItem, {
                key: 1,
                input: selectedProjectId.value,
                "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((selectedProjectId).value = $event)),
                label: _unref(t)('labels.project'),
                name: "project",
                "input-type": "select",
                options: projectOptions.value,
                errors: []
              }, null, 8, ["input", "label", "options"]))
            : _createCommentVNode("", true),
          _createVNode(FormItem, {
            input: selectedThemeId.value,
            "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((selectedThemeId).value = $event)),
            label: _unref(t)('labels.theme'),
            name: "theme",
            "input-type": "select",
            options: themeOptions.value,
            errors: []
          }, null, 8, ["input", "label", "options"]),
          (!__props.themeOnly)
            ? (_openBlock(), _createBlock(FormItem, {
                key: 2,
                input: selectedRoleId.value,
                "onUpdate:input": _cache[2] || (_cache[2] = ($event: any) => ((selectedRoleId).value = $event)),
                label: _unref(t)('labels.role'),
                name: "role",
                "input-type": "select",
                options: roleOptions.value,
                errors: []
              }, null, 8, ["input", "label", "options"]))
            : _createCommentVNode("", true),
          (selectedProjectHasNoRoles.value && !__props.themeOnly)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, " This user has no active role on the selected project. "))
            : _createCommentVNode("", true),
          (!__props.themeOnly)
            ? (_openBlock(), _createBlock(FormItem, {
                key: 4,
                input: duration.value,
                "onUpdate:input": _cache[3] || (_cache[3] = ($event: any) => ((duration).value = $event)),
                label: _unref(t)('labels.duration'),
                name: "duration",
                "input-type": "basic",
                errors: []
              }, null, 8, ["input", "label"]))
            : _createCommentVNode("", true),
          (!__props.themeOnly)
            ? (_openBlock(), _createBlock(FormItem, {
                key: 5,
                input: notes.value,
                "onUpdate:input": _cache[4] || (_cache[4] = ($event: any) => ((notes).value = $event)),
                label: _unref(t)('labels.notes'),
                name: "notes",
                "input-type": "textArea",
                rows: 4,
                "max-rows": 12,
                errors: []
              }, null, 8, ["input", "label"]))
            : _createCommentVNode("", true)
        ], 32)
      ]),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)("loading")), 1))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(BasicButton, {
        class: "mutate-button",
        type: "submit",
        form: "mutate-form",
        disabled: isLoading.value || !selectedThemeId.value || selectedThemeId.value === ''
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(__props.timesheet.id
            ? _unref(t)("timesheets.buttons.update")
            : _unref(t)("timesheets.buttons.create")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      (__props.timesheet.id && !__props.themeOnly)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            class: _normalizeClass(['mutate-button', 'delete-button']),
            disabled: isLoading.value,
            onClick: _withModifiers(handleDeleteTimesheet, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("timesheets.buttons.delete")), 1)
            ]),
            _: 1
          }, 8, ["disabled"]))
        : _createCommentVNode("", true),
      _createVNode(BasicButton, {
        class: "mutate-button",
        disabled: isLoading.value,
        onClick: closeModal
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ]),
    _: 1
  }))
}
}

})